import { lazy, Suspense, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Backend } from '../net/backend';
import { useAppUpdate } from '../pwa/update';
import { toast } from '../state/toastStore';
import { LiveRegions, Toaster } from '../ui/Toaster';
import { BackendContext } from './backendContext';
import { ErrorBoundary } from './ErrorBoundary';
import { AppRoutes } from './routes';

const DemoPanel = lazy(() => import('../dev/DemoPanel').then((m) => ({ default: m.DemoPanel })));

/** Root: backend (live or demo), routes, and the body-level toast/live-region layer. */
export function App({ backend }: { backend: Backend }) {
  const waiting = useAppUpdate((s) => s.waiting);
  useEffect(() => {
    if (!waiting) return;
    // §9.3: never auto-reload mid-order; the player chooses when.
    const id = toast.info('A new version is available.', {
      persist: true,
      action: { label: 'Reload', run: () => useAppUpdate.getState().apply() },
    });
    return () => toast.dismiss(id);
  }, [waiting]);

  return (
    <BackendContext.Provider value={backend}>
      <ErrorBoundary onReset={() => location.assign(backend.mode === 'demo' ? '/demo' : '/')}>
        <AppRoutes />
      </ErrorBoundary>
      {/* Outside #root so toasts stay readable while a dialog makes the app inert. */}
      {createPortal(
        <>
          <Toaster />
          <LiveRegions />
        </>,
        document.body,
      )}
      {backend.mode === 'demo' && (
        <Suspense fallback={null}>
          <DemoPanel />
        </Suspense>
      )}
    </BackendContext.Provider>
  );
}
